import type { SessionRole } from '../../types';
import { trackEvent } from './analytics';

const STORAGE_KEY = 'analytics_offline_play_v1';
const MAX_COUNT = 999;
type Counts = { games: number; rounds: number };

function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? Math.min(value, MAX_COUNT)
    : 0;
}

// Callers wrap storage access, parsing and writes together: unavailable storage
// must never interrupt gameplay. Only these two aggregate counts are retained.
function readCounts(): Counts {
  const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  const stored =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Partial<Counts>)
      : {};
  return { games: count(stored.games), rounds: count(stored.rounds) };
}

export function recordOfflinePlay(kind: keyof Counts, role: SessionRole): void {
  if (
    typeof navigator === 'undefined' ||
    navigator.onLine !== false ||
    role === 'viewer'
  )
    return;
  try {
    const counts = readCounts();
    counts[kind] = Math.min(counts[kind] + 1, MAX_COUNT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(counts));
  } catch {
    /* Storage disabled, full or corrupt: skip counting. */
  }
}

/** Hand off once to the best-effort gateway; this is not a vendor receipt. */
export function syncOfflinePlay(): void {
  if (typeof navigator === 'undefined' || navigator.onLine !== true) return;
  try {
    const counts = readCounts();
    if (counts.games === 0 && counts.rounds === 0) return;
    trackEvent('offline_play_synced', counts);
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Storage failure must never break startup or reconnect. */
  }
}
